import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getEmailThreads, syncEmailThreads, replyEmailThread, markThreadRead,
  type EmailThread,
} from '../../api/emailThreads';

interface Props {
  contactId?: string;
  mandateId?: string;
  toEmail?: string | null;   // vorausgefüllte Empfänger-E-Mail
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function ThreadBubble({ thread, onMarkRead }: { thread: EmailThread; onMarkRead: (id: string) => void }) {
  const isSent = thread.direction === 'sent';
  const [expanded, setExpanded] = useState(false);
  const preview = thread.body_text.slice(0, 300);
  const needsExpand = thread.body_text.length > 300;

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', alignItems: isSent ? 'flex-end' : 'flex-start', marginBottom: '16px' }}
      onClick={() => { if (!thread.is_read) onMarkRead(thread.id); }}
    >
      <div style={{
        maxWidth: '80%', padding: '12px 16px', borderRadius: '12px',
        background: isSent ? 'var(--accent)' : 'var(--surface2)',
        color: isSent ? '#fff' : 'var(--text)',
        border: isSent ? 'none' : '1px solid var(--border)',
        position: 'relative', cursor: !thread.is_read ? 'pointer' : 'default',
      }}>
        {/* Ungelesen-Punkt */}
        {!thread.is_read && (
          <span style={{
            position: 'absolute', top: '-5px', right: '-5px',
            width: '10px', height: '10px', borderRadius: '50%',
            background: '#f44336', border: '2px solid var(--surface)',
          }} />
        )}

        {/* Meta */}
        <div style={{ fontSize: '11px', opacity: 0.7, marginBottom: '5px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ fontFamily: 'DM Mono, monospace' }}>
            {isSent ? `➜ ${thread.to_email}` : `← ${thread.from_email}`}
          </span>
          <span>·</span>
          <span>{formatTime(thread.occurred_at)}</span>
          {!thread.is_read && (
            <span style={{ background: '#f44336', color: '#fff', fontSize: '9px', padding: '1px 5px', borderRadius: '10px', fontWeight: 600 }}>
              NEU
            </span>
          )}
        </div>

        {/* Betreff */}
        <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px', opacity: 0.9 }}>
          {thread.subject}
        </div>

        {/* Text */}
        <div style={{ fontSize: '13px', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {expanded ? thread.body_text : preview}
          {needsExpand && !expanded && ' …'}
        </div>
        {needsExpand && (
          <button
            onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
            style={{ marginTop: '6px', fontSize: '11px', background: 'none', border: 'none', cursor: 'pointer', opacity: 0.7, color: 'inherit', padding: 0 }}
          >
            {expanded ? '▲ Weniger' : '▼ Mehr anzeigen'}
          </button>
        )}

        {/* Anhänge */}
        {thread.attachment_names.length > 0 && (
          <div style={{ marginTop: '8px', fontSize: '11px', opacity: 0.8 }}>
            📎 {thread.attachment_names.join(' · ')}
          </div>
        )}
      </div>
    </div>
  );
}

export function EmailThreadPanel({ contactId, mandateId, toEmail }: Props) {
  const queryClient = useQueryClient();
  const queryKey = ['email-threads', contactId ?? '', mandateId ?? ''];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => getEmailThreads({ contact_id: contactId, mandate_id: mandateId }),
    enabled: !!(contactId || mandateId),
    staleTime: 60_000,
  });

  const syncMut = useMutation({
    mutationFn: syncEmailThreads,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey }),
  });

  const replyMut = useMutation({
    mutationFn: replyEmailThread,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      setBody('');
    },
  });

  const markReadMut = useMutation({
    mutationFn: markThreadRead,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey }),
  });

  const threads = data?.data ?? [];
  const unread = threads.filter(t => !t.is_read).length;
  const lastThread = threads[threads.length - 1];
  const replySubject = lastThread
    ? (lastThread.subject.startsWith('Re:') ? lastThread.subject : `Re: ${lastThread.subject}`)
    : '';

  const [replyTo, setReplyTo] = useState(toEmail ?? '');
  const [body, setBody] = useState('');

  const handleSend = () => {
    if (!body.trim() || !replyTo.trim()) return;
    replyMut.mutate({
      contact_id: contactId,
      mandate_id: mandateId,
      to_email: replyTo,
      subject: replySubject || 'Nachricht',
      body_text: body,
      in_reply_to: lastThread?.message_id ?? undefined,
    });
  };

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <span className="card-title">E-Mail-Verlauf</span>
          {unread > 0 && (
            <span style={{
              background: '#f44336', color: '#fff', fontSize: '10px', fontWeight: 700,
              padding: '2px 7px', borderRadius: '10px',
            }}>
              {unread} neu
            </span>
          )}
        </div>
        <button
          onClick={() => syncMut.mutate()}
          disabled={syncMut.isPending}
          style={{
            padding: '5px 12px', fontSize: '12px', background: 'var(--surface)',
            border: '1px solid var(--border2)', borderRadius: 'var(--radius)',
            cursor: syncMut.isPending ? 'not-allowed' : 'pointer', color: 'var(--text2)',
            display: 'flex', gap: '5px', alignItems: 'center',
          }}
        >
          {syncMut.isPending ? '⏳ Lädt…' : '↻ Posteingang abrufen'}
        </button>
      </div>

      <div className="card-body">
        {/* Sync-Feedback */}
        {syncMut.isSuccess && (
          <div style={{ padding: '8px 12px', marginBottom: '12px', borderRadius: 'var(--radius)', fontSize: '12px',
            background: 'var(--surface2)', color: 'var(--text3)',
          }}>
            {syncMut.data.alreadyRunning
              ? 'Sync läuft bereits im Hintergrund …'
              : 'Sync läuft im Hintergrund — Liste in wenigen Sekunden aktualisieren.'}
          </div>
        )}
        {syncMut.isError && (
          <div style={{ padding: '8px 12px', marginBottom: '12px', borderRadius: 'var(--radius)', fontSize: '12px',
            background: 'var(--accent-light)', color: 'var(--accent)' }}>
            ⚠️ Sync fehlgeschlagen — bitte IMAP-Einstellungen prüfen.
          </div>
        )}

        {/* Thread-Verlauf */}
        {isLoading ? (
          <div style={{ textAlign: 'center', color: 'var(--text3)', padding: '32px', fontSize: '13px' }}>Lädt…</div>
        ) : threads.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--text3)', padding: '32px', fontSize: '13px' }}>
            Noch keine E-Mails vorhanden.<br />
            <span style={{ fontSize: '12px' }}>Über "Posteingang abrufen" neue Nachrichten importieren oder unten eine Nachricht schreiben.</span>
          </div>
        ) : (
          <div style={{ marginBottom: '20px', maxHeight: '400px', overflowY: 'auto', padding: '4px' }}>
            {threads.map(t => (
              <ThreadBubble key={t.id} thread={t} onMarkRead={id => markReadMut.mutate(id)} />
            ))}
          </div>
        )}

        {/* Antwort-Composer */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '16px', display: 'grid', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: 'var(--text3)', width: '24px', flexShrink: 0 }}>An:</span>
            <input
              type="email"
              value={replyTo}
              onChange={e => setReplyTo(e.target.value)}
              placeholder="empfaenger@beispiel.de"
              style={{
                flex: 1, padding: '7px 10px', fontSize: '13px',
                background: 'var(--surface2)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius)', color: 'var(--text)', fontFamily: 'DM Mono, monospace',
              }}
            />
          </div>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder={replySubject ? `Antwort auf "${replySubject}"…` : 'Nachricht schreiben…'}
            rows={4}
            style={{
              width: '100%', padding: '10px', fontSize: '13px',
              background: 'var(--surface2)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius)', color: 'var(--text)', resize: 'vertical',
              boxSizing: 'border-box', fontFamily: 'inherit',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '11px', color: 'var(--text3)' }}>
              {replySubject && <span>Betreff: <em>{replySubject}</em></span>}
            </div>
            <button
              onClick={handleSend}
              disabled={replyMut.isPending || !body.trim() || !replyTo.trim()}
              style={{
                padding: '8px 20px', fontSize: '13px', fontWeight: 600,
                background: (!body.trim() || !replyTo.trim()) ? 'var(--surface2)' : 'var(--accent)',
                color: (!body.trim() || !replyTo.trim()) ? 'var(--text3)' : '#fff',
                border: 'none', borderRadius: 'var(--radius)',
                cursor: (replyMut.isPending || !body.trim() || !replyTo.trim()) ? 'not-allowed' : 'pointer',
                opacity: replyMut.isPending ? 0.7 : 1,
              }}
            >
              {replyMut.isPending ? '⏳ Wird gesendet…' : '✉ Senden'}
            </button>
          </div>
          {replyMut.isError && (
            <div style={{ fontSize: '12px', color: 'var(--accent)', padding: '6px 10px', background: 'var(--accent-light)', borderRadius: 'var(--radius)' }}>
              ⚠️ Fehler beim Senden. Bitte SMTP-Einstellungen prüfen.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
